/**
 * ダッシュボードの色付け。現行スプレッドシート「toそうしさん日報」の条件付き書式ルールを xlsx から抽出して再現(2026-09-09)。
 *
 * 日次【集計】⑨ のルール:
 *  - RANK: S=青(#0000FF・白字) / A=赤(#FF0000・白字) / B+=橙(#FF9900・白字) / B=薄橙(#FCE5CD) / B-=薄緑(#D9EAD3) / C=灰(#CCCCCC)
 *  - 時間別取得件数: 3件以上=赤(#FF0000・白字)、0=非表示
 *  - 1通話あたりの取得率: 5%以上=薄橙(#FCE5CD)
 *  - 1取得あたりの通話時間: 15分以下=薄橙 / 1取得あたりの稼働時間: 24分以下=薄橙
 *  - 1時間あたりの見込取得数: 非ホワイト 2.2超=薄青(#A4C2F4)・1.6未満=赤(#E06666・白字) / ホワイト 0.4超=薄青・0.2以下=赤
 *  - 稼動率(%): 80以上=薄橙
 * 月次【スタッフ別】: アポ/h 2.5以上=赤(白字)、2.0〜2.49=橙(白字)
 * 原価率シート: 30%未満=青(#4285F4・白字) / 30〜40%未満=薄青(#A4C2F4) / 40〜45%=水色(#C9DAF8) / 45〜50%=薄黄(#FFF2CC) /
 *              60〜90%未満=灰(#B7B7B7) / 90%以上=黒(赤字)
 *
 * 各指標の「説明(HELP)」と「今の値の判定(〜Verdict)」は HelpTip の吹き出しに出す(⑦指示)。
 */
import type { Help, Verdict } from '@core/components/HelpTip';
import type { BusinessType } from '@core/lib/types';
import type { Rank } from './calc';
import { fmtDuration, fmtNum, fmtPct } from './calc';

export type CfClass = string;

export const THRESHOLDS = {
  hourlyHot: 3, // 時間別取得件数 ≥3 → 赤
  hitRate: 5, // 取得率 ≥5% → 薄橙
  talkPerApptSec: 15 * 60, // 1取得あたり通話時間 ≤15分 → 薄橙
  workPerApptSec: 24 * 60, // 1取得あたり稼働時間 ≤24分 → 薄橙
  apph: { good: 2.2, bad: 1.6 }, // 非ホワイト: >2.2 薄青 / <1.6 赤
  apphWhite: { good: 0.4, bad: 0.2 }, // ホワイト: >0.4 薄青 / ≤0.2 赤
  utilization: 80, // 稼動率 ≥80% → 薄橙
  monthlyApph: { red: 2.5, orange: 2.0 },
} as const;

const NONE = 'sw-none';

/** 項目名にカーソルを合わせたときの説明(指標の意味・判定の向き・色の意味) */
export const HELP = {
  rank: {
    title: 'RANK',
    what: '1時間あたりの見込取得数(取得数 ÷ 稼働合計(受付＋通話＋後処理))から判定。不用品(ホワイト)は10倍した値で判定',
    better: 'S が最も良く、C が最も悪い',
    colors: [
      { cls: 'cf-rank-s', range: 'S: 5.0以上', meaning: 'とても良い' },
      { cls: 'cf-rank-a', range: 'A: 3.5〜5.0', meaning: '良い' },
      { cls: 'cf-rank-bp', range: 'B+: 2.5〜3.5', meaning: 'やや良い' },
      { cls: 'cf-rank-bm', range: 'B-: 2.0〜2.5', meaning: '普通' },
      { cls: 'cf-rank-c', range: 'C: 2.0未満', meaning: '悪い' },
    ],
    source: '現行シート「日次【集計】」',
  },
  hourly: {
    title: '時間別取得件数',
    what: 'その時台(9時台〜19時台)に取得した件数',
    better: '多いほど良い',
    colors: [
      { cls: 'cf-hot', range: '3件以上', meaning: '良い(赤)' },
      { cls: NONE, range: '1〜2件', meaning: '普通' },
      { cls: NONE, range: '0件', meaning: '表示しない' },
    ],
    source: '現行シート「日次【集計】」',
  },
  hitRate: {
    title: '取得率',
    what: '取得数 ÷ 通話回数(1通話あたりの取得率)',
    better: '高いほど良い',
    colors: [
      { cls: 'cf-peach', range: '5%以上', meaning: '良い(薄橙)' },
      { cls: NONE, range: '5%未満', meaning: '普通' },
    ],
    source: '現行シート「日次【集計】」',
  },
  talkPerAppt: {
    title: '1取得あたりの通話時間',
    what: '通話時間 ÷ 取得数(1件取るのにかかった通話時間)',
    better: '短いほど良い',
    colors: [
      { cls: 'cf-peach', range: '15分以下', meaning: '良い(薄橙)' },
      { cls: NONE, range: '15分超', meaning: '普通' },
    ],
    source: '現行シート「日次【集計】」',
  },
  workPerAppt: {
    title: '1取得あたりの稼働時間',
    what: '(受付＋通話＋後処理) ÷ 取得数(1件取るのにかかった稼働時間)',
    better: '短いほど良い',
    colors: [
      { cls: 'cf-peach', range: '24分以下', meaning: '良い(薄橙)' },
      { cls: NONE, range: '24分超', meaning: '普通' },
    ],
    source: '現行シート「日次【集計】」',
  },
  apph: {
    title: '1時間あたりの見込取得数(見込/h)',
    what: '取得数 ÷ 稼働合計(受付＋通話＋後処理)。RANKの判定にも使う',
    better: '高いほど良い',
    colors: [
      { cls: 'cf-blue', range: '給湯器 2.2超 ／ 不用品 0.4超', meaning: '良い(薄青)' },
      { cls: NONE, range: 'その間', meaning: '普通' },
      { cls: 'cf-red', range: '給湯器 1.6未満 ／ 不用品 0.2以下', meaning: '悪い(赤)' },
    ],
    source: '現行シート「日次【集計】」',
  },
  utilization: {
    title: '稼動率',
    what: '稼働時間 ÷ ログイン時間',
    better: '高いほど良い',
    colors: [
      { cls: 'cf-peach', range: '80%以上', meaning: '良い(薄橙)' },
      { cls: NONE, range: '80%未満', meaning: '普通' },
    ],
    source: '現行シート「日次【集計】」',
  },
  monthlyApph: {
    title: 'アポ/h(月次)',
    what: '取得数 ÷ 稼働時間(月次【スタッフ別】の判定)',
    better: '高いほど良い',
    colors: [
      { cls: 'cf-hot', range: '2.5以上', meaning: 'とても良い(赤)' },
      { cls: 'cf-orange', range: '2.0〜2.49', meaning: '良い(橙)' },
      { cls: NONE, range: '2.0未満', meaning: '普通' },
    ],
    source: '現行シート「月次【スタッフ別】」',
  },
  costRate: {
    title: '原価率',
    what: '報酬(原価) ÷ 売上。売上は納品ベースの実績があれば実績、無ければ取得ベースの見込み',
    better: '低いほど良い(全体の目標は 44.5%)',
    colors: [
      { cls: 'cf-cr-blue', range: '30%未満', meaning: 'とても良い(青)' },
      { cls: 'cf-cr-lightblue', range: '30〜40%', meaning: '良い(薄青)' },
      { cls: 'cf-cr-sky', range: '40〜45%', meaning: '目標内(水色)' },
      { cls: 'cf-cr-yellow', range: '45〜50%', meaning: 'やや高い(薄黄)' },
      { cls: NONE, range: '50〜60%', meaning: '高い' },
      { cls: 'cf-cr-grey', range: '60〜90%', meaning: '悪い(灰)' },
      { cls: 'cf-cr-black', range: '90%以上', meaning: 'とても悪い(黒)' },
    ],
    source: '現行シート「原価率」',
  },
  grossRate: {
    title: '粗利率',
    what: '粗利(売上 − 報酬) ÷ 売上',
    better: '高いほど良い',
  },
  revenue: {
    title: '売上見込み',
    what: '取得件数 × 売上単価(割振会社の単価)。納品前のため見込み',
    better: '高いほど良い',
  },
  actualSales: {
    title: '売上実績',
    what: '納品件数(訪問数) × 売上単価。取得したアポは翌日に各会社へ納品し、納品前のキャンセル分は減る',
    better: '高いほど良い',
  },
  cancelRate: {
    title: 'キャンセル率',
    what: 'キャンセル合計(在宅＋出勤) ÷ 実績アポ数',
    better: '低いほど良い',
  },
  wage: {
    title: '報酬',
    what: '時給(出勤・在宅時給・タイミー)または件数単価(在宅歩合)。オペレーターマスタのその日の値。管理者は表示しない',
    better: '—',
  },
  workSec: {
    title: '稼働合計',
    what: '受付＋通話＋後処理(休憩・離席は含まない)。実稼働(h)はこれを時間にしたもの',
    better: '—',
  },
  pay: {
    title: '報酬額見込',
    what: '時給×実稼働(出勤は交通費込み)、件数単価×取得数(在宅歩合)、時給×稼働合計(在宅時給)の参考値。確定の人件費は「納品件数の入力」で持つ',
    better: '低いほど原価率は良い',
  },
} satisfies Record<string, Help>;

// ---- セルの色(現行シートの条件付き書式) -----------------------------------------

export function rankClass(rank: Rank): CfClass {
  switch (rank) {
    case 'S':
      return 'cf-rank-s';
    case 'A':
      return 'cf-rank-a';
    case 'B+':
      return 'cf-rank-bp';
    case 'B-':
      return 'cf-rank-bm';
    case 'C':
      return 'cf-rank-c';
    default:
      return '';
  }
}

export function hourlyClass(n: number): CfClass {
  if (n >= THRESHOLDS.hourlyHot) return 'cf-hot';
  if (n === 0) return 'cf-zero';
  return '';
}

export function hitRateClass(pct: number | null): CfClass {
  return pct !== null && pct >= THRESHOLDS.hitRate ? 'cf-peach' : '';
}

export function talkPerApptClass(sec: number | null): CfClass {
  return sec !== null && sec <= THRESHOLDS.talkPerApptSec ? 'cf-peach' : '';
}

export function workPerApptClass(sec: number | null): CfClass {
  return sec !== null && sec <= THRESHOLDS.workPerApptSec ? 'cf-peach' : '';
}

export function apphClass(apph: number | null, business: BusinessType): CfClass {
  if (apph === null) return '';
  const t = business === 'fuyohin' ? THRESHOLDS.apphWhite : THRESHOLDS.apph;
  if (apph > t.good) return 'cf-blue';
  if (business === 'fuyohin' ? apph <= t.bad : apph < t.bad) return 'cf-red';
  return '';
}

export function utilizationClass(pct: number | null): CfClass {
  return pct !== null && pct >= THRESHOLDS.utilization ? 'cf-peach' : '';
}

/** 月次のアポ/h(月次【スタッフ別】シート) */
export function monthlyApphClass(apph: number | null): CfClass {
  if (apph === null) return '';
  if (apph >= THRESHOLDS.monthlyApph.red) return 'cf-hot';
  if (apph >= THRESHOLDS.monthlyApph.orange) return 'cf-orange';
  return '';
}

/** 原価率(%)の帯色(原価率シート) */
export function costRateClass(pct: number | null): CfClass {
  if (pct === null || !Number.isFinite(pct)) return '';
  if (pct < 30) return 'cf-cr-blue';
  if (pct < 40) return 'cf-cr-lightblue';
  if (pct <= 45) return 'cf-cr-sky';
  if (pct <= 50) return 'cf-cr-yellow';
  if (pct < 60) return '';
  if (pct < 90) return 'cf-cr-grey';
  return 'cf-cr-black';
}

// ---- 今の値の判定(吹き出しの先頭に出す) ------------------------------------------

export function rankVerdict(rank: Rank): Verdict | null {
  switch (rank) {
    case 'S':
      return { level: 'good', reason: 'S: 見込/h が 5.0以上' };
    case 'A':
      return { level: 'good', reason: 'A: 見込/h が 3.5〜5.0' };
    case 'B+':
      return { level: 'good', reason: 'B+: 見込/h が 2.5〜3.5' };
    case 'B-':
      return { level: 'normal', reason: 'B-: 見込/h が 2.0〜2.5' };
    case 'C':
      return { level: 'bad', reason: 'C: 見込/h が 2.0未満' };
    default:
      return null;
  }
}

export function hourlyVerdict(n: number): Verdict | null {
  if (n >= THRESHOLDS.hourlyHot) return { level: 'good', reason: `${n}件: 3件以上なので赤` };
  if (n > 0) return { level: 'normal', reason: `${n}件: 3件未満(色なし)` };
  return null;
}

export function hitRateVerdict(pct: number | null): Verdict | null {
  if (pct === null) return null;
  return pct >= THRESHOLDS.hitRate ? { level: 'good', reason: `${fmtPct(pct)}: 5%以上なので薄橙` } : { level: 'normal', reason: `${fmtPct(pct)}: 5%未満(色なし)` };
}

export function talkPerApptVerdict(sec: number | null): Verdict | null {
  if (sec === null) return null;
  return sec <= THRESHOLDS.talkPerApptSec ? { level: 'good', reason: `${fmtDuration(sec)}: 15分以下なので薄橙` } : { level: 'normal', reason: `${fmtDuration(sec)}: 15分を超えている(色なし)` };
}

export function workPerApptVerdict(sec: number | null): Verdict | null {
  if (sec === null) return null;
  return sec <= THRESHOLDS.workPerApptSec ? { level: 'good', reason: `${fmtDuration(sec)}: 24分以下なので薄橙` } : { level: 'normal', reason: `${fmtDuration(sec)}: 24分を超えている(色なし)` };
}

export function apphVerdict(apph: number | null, business: BusinessType): Verdict | null {
  if (apph === null) return null;
  const t = business === 'fuyohin' ? THRESHOLDS.apphWhite : THRESHOLDS.apph;
  const kind = business === 'fuyohin' ? '不用品' : '給湯器';
  if (apph > t.good) return { level: 'good', reason: `${fmtNum(apph)}: ${kind}の基準 ${t.good} を超えているので薄青` };
  if (business === 'fuyohin' ? apph <= t.bad : apph < t.bad) return { level: 'bad', reason: `${fmtNum(apph)}: ${kind}の基準 ${t.bad} を下回るので赤` };
  return { level: 'normal', reason: `${fmtNum(apph)}: ${t.bad}〜${t.good} の間(色なし)` };
}

export function costRateVerdict(pct: number | null, target: number | null = 44.5): Verdict | null {
  if (pct === null || !Number.isFinite(pct)) return null;
  const v = fmtPct(pct);
  if (pct < 30) return { level: 'good', reason: `${v}: 30%未満(青)` };
  if (pct < 40) return { level: 'good', reason: `${v}: 30〜40%(薄青)` };
  if (pct <= 45) return { level: target !== null && pct <= target ? 'good' : 'normal', reason: `${v}: 40〜45%(水色)${target !== null ? `。目標 ${target}%${pct <= target ? '以内' : '超'}` : ''}` };
  if (pct <= 50) return { level: 'normal', reason: `${v}: 45〜50%(薄黄)。目標を超えている` };
  if (pct < 60) return { level: 'bad', reason: `${v}: 50〜60%(色なし)。目標を超えている` };
  if (pct < 90) return { level: 'bad', reason: `${v}: 60〜90%(灰)` };
  return { level: 'bad', reason: `${v}: 90%以上(黒)。売上とほぼ同じか、それ以上の報酬` };
}

export function grossRateVerdict(pct: number | null): Verdict | null {
  if (pct === null || !Number.isFinite(pct)) return null;
  if (pct >= 55.5) return { level: 'good', reason: `${fmtPct(pct)}: 原価率が目標(44.5%)以内` };
  if (pct >= 40) return { level: 'normal', reason: `${fmtPct(pct)}: 原価率が目標をやや超えている` };
  return { level: 'bad', reason: `${fmtPct(pct)}: 原価率が高い` };
}
